import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Eye, EyeOff, Archive, ArchiveRestore, Trash2, X, RefreshCw } from "lucide-react";
import { usePermissions } from "@/context/PermissionsContext";
import { SupabaseNotification } from "@/types/notifications";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/notifications/StatusBadges";

interface BulkActionsToolbarProps {
  selectedCount: number;
  selectedNotifications: SupabaseNotification[];
  viewMode: "active" | "archived";
  onMarkAsRead: () => void;
  onMarkAsUnread: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
  onChangeStatus: (status: string) => void;
  onClearSelection: () => void;
  isLoading?: boolean;
}

const BulkActionsToolbar = ({
  selectedCount,
  selectedNotifications,
  viewMode,
  onMarkAsRead,
  onMarkAsUnread,
  onArchive,
  onRestore,
  onDelete,
  onChangeStatus,
  onClearSelection,
  isLoading = false
}: BulkActionsToolbarProps) => {
  const { hasPermission } = usePermissions();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const canWrite = hasPermission("notifications", "write");
  const canDelete = hasPermission("notifications", "delete");

  if (selectedCount === 0) return null;

  const handleDelete = () => {
    setShowDeleteDialog(false);
    onDelete();
  };

  return (
    <div className="sticky top-0 z-20 bg-background border border-border rounded-lg p-3 mb-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {selectedCount} melding(en) geselecteerd
          </span>
          
          <div className="flex items-center gap-1 ml-4">
            {canWrite && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onMarkAsRead}
                  disabled={isLoading}
                  className="h-8"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Gelezen
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onMarkAsUnread}
                  disabled={isLoading}
                  className="h-8"
                >
                  <EyeOff className="h-3 w-3 mr-1" />
                  Ongelezen
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isLoading}
                      className="h-8"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Wijzig Status
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64">
                    <DropdownMenuItem onClick={() => onChangeStatus("actie_luijten")}>
                      <StatusBadge status="actie_luijten" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onChangeStatus("actie_decoprof")}>
                      <StatusBadge status="actie_decoprof" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onChangeStatus("actie_winkel_ridderkerk")}>
                      <StatusBadge status="actie_winkel_ridderkerk" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onChangeStatus("afwachting_klant")}>
                      <StatusBadge status="afwachting_klant" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onChangeStatus("afwachting_voorraad")}>
                      <StatusBadge status="afwachting_voorraad" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onChangeStatus("afwachting_voorraad_ridderkerk")}>
                      <StatusBadge status="afwachting_voorraad_ridderkerk" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onChangeStatus("afwijkende_verzenddatum")}>
                      <StatusBadge status="afwijkende_verzenddatum" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onChangeStatus("afgerond")}>
                      <StatusBadge status="afgerond" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {viewMode === "active" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onArchive}
                    disabled={isLoading}
                    className="h-8"
                  >
                    <Archive className="h-3 w-3 mr-1" />
                    Archiveren
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRestore}
                    disabled={isLoading}
                    className="h-8"
                  >
                    <ArchiveRestore className="h-3 w-3 mr-1" />
                    Herstellen
                  </Button>
                )}
              </>
            )}

            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                disabled={isLoading}
                className="h-8 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Verwijderen
              </Button>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="h-8"
        >
          <X className="h-3 w-3 mr-1" />
          Deselecteren
        </Button>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Meldingen verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {selectedCount} melding(en) wilt verwijderen? 
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BulkActionsToolbar;